/**
 * Hire businesses (2026-09, Moving Ads camera hire) — the shared vocabulary
 * for products that go out on hire and the house rules a hire business sets.
 *
 *   products.hire        — the rates on a product (ProductHire). The till
 *                          computes the fee from them; the website shows them.
 *   brands.hireSettings  — the switch and the house rules (HireSettings).
 *
 * Pure functions only: the till, the storefront and the Convex functions all
 * price a hire through hireFeeFor so the number is the same everywhere.
 */

export type HirePricingType = "per_day" | "flat";

export interface ProductHire {
  enabled: boolean;
  /** Default per_day. */
  pricingType?: HirePricingType;
  dailyRate?: number;
  /** Optional: a cheaper week. Whole weeks use it, the rest of the days the daily rate. */
  weeklyRate?: number;
  /** One fee for the whole hire, however long. */
  flatRate?: number;
  /** Suggested cash security per unit. */
  deposit?: number;
  minDays?: number;
  /** Not for sale — the website hides the selling price and offers hire only. */
  hireOnly?: boolean;
}

export type SecurityKind = "cash" | "national_id" | "passport" | "driving_license" | "other";

export interface HireDepositRule {
  mode: "none" | "percent" | "fixed";
  /** percent: % of the item's value · fixed: UGX per unit. */
  value?: number;
}

export interface HireSettings {
  /** "This business hires things out" — the defaults follow. */
  enabled?: boolean;
  /** A document hire must have an ID photo on file before the item leaves. */
  requireId?: boolean;
  requireGuarantor?: boolean;
  /**
   * How many next of kin a hire must name (Moving Ads, 2026-09-23: "we
   * usually require at least 2"). 0/absent falls back to requireGuarantor
   * (1 when on) — see guarantorsRequired().
   */
  guarantorsRequired?: number;
  defaultSecurityType?: SecurityKind;
  /** Fallback deposit when the product has none. */
  depositRule?: HireDepositRule;
  /** Suggested at return: UGX per day late. */
  lateFeePerDay?: number;
  /** "Request to hire" on the website. Default on. */
  websiteRequests?: boolean;
}

export const DAY_MS = 24 * 60 * 60 * 1000;

export const SECURITY_KINDS: ReadonlyArray<{ value: SecurityKind; label: string }> = [
  { value: "cash", label: "Cash deposit" },
  { value: "national_id", label: "National ID" },
  { value: "passport", label: "Passport" },
  { value: "driving_license", label: "Driving permit" },
  { value: "other", label: "Other document" },
];

export function securityKindLabel(kind: string | undefined): string {
  return SECURITY_KINDS.find((k) => k.value === kind)?.label || (kind ? kind : "—");
}

const num = (x: unknown): number => {
  const n = Number(x);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export function hireOf(product: any): ProductHire | null {
  const h = product?.hire;
  if (!h || typeof h !== "object" || h.enabled !== true) return null;
  return h as ProductHire;
}

/** The rate the item is priced by, or 0 when the shop has not set one yet. */
export function hireRateOf(product: any): number {
  const h = hireOf(product);
  if (!h) return 0;
  return (h.pricingType || "per_day") === "flat" ? num(h.flatRate) : num(h.dailyRate);
}

/**
 * Offered for hire: enabled AND priced — or hire-only, which is for hire by
 * definition even before the shop types a rate (the desk quotes it).
 * An "enabled" item that is also sold, with no rate yet, is not offered.
 */
export function isForHire(product: any): boolean {
  const h = hireOf(product);
  if (!h) return false;
  return hireRateOf(product) > 0 || h.hireOnly === true;
}

export function isHireOnly(product: any): boolean {
  return hireOf(product)?.hireOnly === true;
}

/** Calendar days between two instants, never less than one. */
export function hireDays(startMs: number, endMs: number): number {
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return 1;
  return Math.max(1, Math.ceil((endMs - startMs) / DAY_MS));
}

/** The fee for `qty` units over `days`, or null when the item is not for hire. */
export function hireFeeFor(product: any, days: number, qty = 1): number | null {
  const h = hireOf(product);
  // No rate yet (a hire-only item the shop quotes by hand) → no figure.
  if (!h || !isForHire(product) || hireRateOf(product) <= 0) return null;
  const d = Math.max(1, Math.round(days || 1));
  const q = Math.max(1, Math.round(qty || 1));
  if ((h.pricingType || "per_day") === "flat") return Math.round(num(h.flatRate) * q);
  const daily = num(h.dailyRate);
  const weekly = num(h.weeklyRate);
  let perUnit: number;
  if (weekly > 0 && d >= 7) {
    const weeks = Math.floor(d / 7);
    const rest = d % 7;
    // A cheaper week must never cost more than the days it covers.
    perUnit = weeks * Math.min(weekly, daily * 7) + rest * daily;
  } else {
    perUnit = d * daily;
  }
  return Math.round(perUnit * q);
}

export function formatUgx(n: number | undefined | null, currency = "UGX"): string {
  return `${currency} ${Math.round(Number(n) || 0).toLocaleString()}`;
}

/** "UGX 50,000 / day", "UGX 40,000 flat", "Rate on request" for a hire-only item without a rate, "" when not for hire. */
export function hireRateLabel(product: any, currency = "UGX"): string {
  const h = hireOf(product);
  if (!h || !isForHire(product)) return "";
  if (hireRateOf(product) <= 0) return "Rate on request";
  if ((h.pricingType || "per_day") === "flat") return `${formatUgx(h.flatRate, currency)} flat`;
  return `${formatUgx(h.dailyRate, currency)} / day`;
}

/** The product's own deposit, else the house rule against the item's value. */
export function suggestedDepositFor(product: any, settings: HireSettings | null | undefined, qty = 1): number {
  const q = Math.max(1, Math.round(qty || 1));
  const own = num(hireOf(product)?.deposit);
  if (own > 0) return Math.round(own * q);
  const rule = settings?.depositRule;
  if (!rule || rule.mode === "none") return 0;
  if (rule.mode === "fixed") return Math.round(num(rule.value) * q);
  const value = num(product?.price);
  return Math.round((value * q * num(rule.value)) / 100);
}

/** What the house suggests for a late return. */
export function lateFeeFor(settings: HireSettings | null | undefined, daysOverdue: number): number {
  const perDay = num(settings?.lateFeePerDay);
  const d = Math.max(0, Math.round(daysOverdue || 0));
  return Math.round(perDay * d);
}

// ─── Kit pieces (what goes out with an item) ────────────────────────────────
// Moving Ads, 2026-09-23: the pieces that go out with a camera — batteries, a
// charger, a lens — are sometimes charged too. A piece may carry a daily
// rate; it is charged only for the pieces that actually go out, per day.

export interface KitPiece {
  name: string;
  qtyOut: number;
  /** UGX per day per piece; absent/0 = comes free with the item. */
  dailyRate?: number;
}

/** What the charged pieces going out with one line add to its fee over `days`. */
export function kitFeeFor(pieces: ReadonlyArray<Partial<KitPiece>> | null | undefined, days: number): number {
  const d = Math.max(1, Math.round(days || 1));
  let sum = 0;
  for (const p of pieces || []) {
    const qty = Math.max(0, Math.round(Number(p?.qtyOut) || 0));
    sum += Math.round(num(p?.dailyRate) * qty * d);
  }
  return sum;
}

/** "with 2 × Battery (UGX 20,000/day), 1 × Charger" — the line under a hired item on its receipt. */
export function kitSummaryText(pieces: ReadonlyArray<Partial<KitPiece>> | null | undefined, currency = "UGX"): string {
  const parts = (pieces || [])
    .filter((p) => p?.name && Number(p.qtyOut) > 0)
    .map((p) => `${Math.round(Number(p.qtyOut))} × ${String(p.name).trim()}${num(p.dailyRate) > 0 ? ` (${formatUgx(p.dailyRate, currency)}/day)` : ""}`);
  return parts.length ? `with ${parts.join(", ")}` : "";
}

// ─── Next of kin ─────────────────────────────────────────────────────────────

export interface NextOfKin {
  name: string;
  phone: string;
  relationship?: string;
  idNumber?: string;
}

/** A next of kin counts once it has a name AND a phone. */
export function isCompleteKin(k: Partial<NextOfKin> | null | undefined): boolean {
  return !!(k && String(k.name || "").trim() && String(k.phone || "").trim());
}

/** How many next of kin this shop wants on every hire (0 = none required). */
export function guarantorsRequired(settings: HireSettings | null | undefined): number {
  const n = Math.round(num(settings?.guarantorsRequired));
  if (n > 0) return Math.min(5, n);
  return settings?.requireGuarantor ? 1 : 0;
}
